import React from 'react';
import { Users, Tag, FileText, Coins, CalendarDays, Package } from 'lucide-react';const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', { 
    style: 'currency', 
    currency: 'IDR', 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

export const SimulasiProduksiReport = React.forwardRef(({ data, dateRange }, ref) => {
  const { pos, totalBiayaProduksi, averageHPP } = data;

  // Group POs by Brand
  const brands = {};
  pos.forEach(po => {
    const brandName = po._computedBrand || po.brand || 'Unknown Brand';
    if (!brands[brandName]) {
      brands[brandName] = {
        name: brandName,
        pic: po._computedPic || po.pic || 'Unknown PIC',
        totalProduksi: 0,
        totalBiaya: 0,
        pos: [],
        packaging: [],
        bahanCair: [],
        itemTambahan: []
      };
    }
    brands[brandName].totalProduksi += po._computedQty || po.qty || 0;
    brands[brandName].totalBiaya += po.totalBiaya || 0;
    brands[brandName].pos.push(po);
    
    // Group items
    if (po.items) {
      po.items.forEach(item => {
      const itemCost = item.isClientProvided ? 0 : (item.qty * item.price);
      const row = {
        name: item.name,
        usage: item.subCategory || '-',
        qty: item.qty,
        cost: itemCost
      };
      if (item.category === 'Packaging') brands[brandName].packaging.push(row);
      else if (item.category === 'Bahan Cair') brands[brandName].bahanCair.push(row);
      else brands[brandName].itemTambahan.push(row);
      });
    }
  });

  const brandList = Object.values(brands);
  const totalBrandCount = brandList.length;
  const uniquePICs = new Set(brandList.map(b => b.pic)).size;
  const totalPOCount = pos.length;
  const totalAllProduksi = brandList.reduce((sum, b) => sum + b.totalProduksi, 0);

  const printDate = new Intl.DateTimeFormat('id-ID', { 
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }).format(new Date()) + ' WIB';

  return (
    <div ref={ref} className="bg-white text-slate-800 w-full min-h-[210mm] p-8 print:p-0 print:bg-white mx-auto relative" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @page { size: A4 landscape; margin: 8mm; }
        .page-break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
        @media print {
          html, body {
            background-color: white !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
      {/* HEADER */}
      <div className="flex justify-between items-start border-b-2 border-[#8C6208] pb-4 mb-6">
        <div className="flex items-center gap-4">
          <img src="/logo.png" alt="Satala Logo" className="w-16 h-16 object-contain" />
          <div>
            <h1 className="text-xl font-black text-[#302621] tracking-wider">SATALA DERMATECH ESSENTIAL</h1>
            <p className="text-sm font-bold text-slate-700">PT. Satala Dermatech Essential</p>
            <p className="text-[10px] text-slate-500 mt-1">Jl. Raya Dramaga No. 88, Dramaga, Kec. Dramaga<br/>Kab. Bogor, Jawa Barat 16680, Indonesia<br/>NPWP: 71.123.456.7-891.000</p>
          </div>
        </div>
        <div className="text-center self-center">
          <h2 className="text-2xl font-black text-[#302621] uppercase tracking-widest">Laporan Simulasi Biaya Produksi</h2>
          <div className="w-16 h-1 bg-[#8C6208] mx-auto mt-2 rounded-full"></div>
        </div>
        <div className="border border-[#E6D5B8] rounded-xl p-3 bg-[#F9F5EC] min-w-[200px]">
          <p className="text-[10px] font-bold text-[#7A695B] uppercase mb-1">Periode Laporan</p>
          <p className="text-xs font-bold text-[#302621] flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#8C6208]" /> {dateRange || 'Semua Waktu'}
          </p>
        </div>
      </div>

      {/* METRICS ROW */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-[#E6D5B8] rounded-xl p-3 flex items-center gap-3 col-span-1">
          <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100 text-[#8C6208]">
            <Users className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-[#7A695B] uppercase">Jumlah PIC</p>
            <p className="text-sm font-black text-[#302621]">{uniquePICs} PICs</p>
            <p className="text-[8px] text-slate-400">Terkait dalam laporan</p>
          </div>
        </div>
        <div className="border border-[#E6D5B8] rounded-xl p-3 flex items-center gap-3 col-span-1">
          <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100 text-[#8C6208]">
            <Tag className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-[#7A695B] uppercase">Jumlah Brand</p>
            <p className="text-sm font-black text-[#302621]">{totalBrandCount} Brand</p>
            <p className="text-[8px] text-slate-400">Dalam laporan</p>
          </div>
        </div>
        <div className="border border-[#E6D5B8] rounded-xl p-3 flex items-center gap-3 col-span-1">
          <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100 text-[#8C6208]">
            <FileText className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-[#7A695B] uppercase">Total PO</p>
            <p className="text-sm font-black text-[#302621]">{totalPOCount} PO</p>
            <p className="text-[8px] text-slate-400">Total Purchase Order</p>
          </div>
        </div>
        <div className="border border-[#E6D5B8] rounded-xl p-3 flex items-center gap-3 col-span-1">
          <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100 text-[#8C6208]">
            <Package className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-[#7A695B] uppercase">Total Produksi</p>
            <p className="text-sm font-black text-[#302621]">{totalAllProduksi.toLocaleString('id-ID')} pcs</p>
            <p className="text-[8px] text-slate-400">Seluruh item diproduksi</p>
          </div>
        </div>
        <div className="border border-[#E6D5B8] rounded-xl p-3 flex items-center gap-3 col-span-2">
          <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100 text-[#8C6208]">
            <Coins className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[9px] font-bold text-[#7A695B] uppercase">Total Biaya Produksi</p>
            <p className="text-sm font-black text-[#302621]">{formatCurrency(totalBiayaProduksi)}</p>
            <p className="text-[8px] text-slate-400">Total seluruh biaya</p>
          </div>
        </div>
      </div>

      <div className="bg-[#8C6208] text-white py-1 px-3 mb-4 text-xs font-bold w-max rounded-sm">
        RINCIAN BIAYA PRODUKSI PER BRAND
      </div>

      {/* BRAND LIST */}
      <div className="space-y-4">
        {brandList.map((brand, idx) => {
          const hpp = Math.round(brand.totalBiaya / brand.totalProduksi);
          const pkgTotal = brand.packaging.reduce((sum, i) => sum + i.cost, 0);
          const bcTotal = brand.bahanCair.reduce((sum, i) => sum + i.cost, 0);
          const itTotal = brand.itemTambahan.reduce((sum, i) => sum + i.cost, 0);
          
          return (
            <div key={idx} className="border border-[#E6D5B8] rounded-xl overflow-hidden page-break-inside-avoid">
              {/* Brand Header */}
              <div className="bg-[#F9F5EC] border-b border-[#E6D5B8] px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="bg-[#8C6208] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                  <span className="font-black text-sm text-[#302621]">{brand.name}</span>
                  <span className="text-[10px] text-slate-500 ml-4">PIC: {brand.pic || 'Tidak Ada'}</span>
                </div>
                <div className="flex items-center gap-6 text-[10px] text-slate-700">
                  <span>Total Produksi: <strong className="text-[#302621]">{brand.totalProduksi.toLocaleString('id-ID')} pcs</strong></span>
                  <span>Total Biaya: <strong className="text-[#302621]">{formatCurrency(brand.totalBiaya)}</strong></span>
                  <span>Rata-rata HPP / pcs: <strong className="text-[#302621]">{formatCurrency(hpp)}</strong></span>
                </div>
              </div>
              
              {/* 3 Columns */}
              <div className="grid grid-cols-3 divide-x divide-[#E6D5B8]">
                {/* Packaging */}
                <div className="flex flex-col h-full">
                  <div className="bg-white py-1 px-3 text-[9px] font-bold text-[#302621] border-b border-[#E6D5B8] uppercase text-center">PACKAGING</div>
                  <table className="w-full text-[9px]">
                    <thead className="bg-[#F9F5EC] text-[#7A695B] border-b border-[#E6D5B8]">
                      <tr>
                        <th className="py-1 px-2 text-left font-bold uppercase">No</th>
                        <th className="py-1 px-2 text-left font-bold uppercase">Item</th>
                        <th className="py-1 px-2 text-center font-bold uppercase">QTY</th>
                        <th className="py-1 px-2 text-right font-bold uppercase">Total Biaya (Rp)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brand.packaging.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 px-2 text-slate-400">{i+1}</td>
                          <td className="py-1 px-2 text-[#302621] break-words">{p.name}</td>
                          <td className="py-1 px-2 text-slate-500 text-center">{p.qty ? p.qty.toLocaleString('id-ID') : '-'}</td>
                          <td className="py-1 px-2 text-right text-[#302621]">{p.cost > 0 ? formatCurrency(p.cost).replace('Rp', '').trim() : '-'}</td>
                        </tr>
                      ))}
                      {brand.packaging.length === 0 && (
                        <tr><td colSpan={4} className="py-2 text-center text-slate-400 italic">Tidak ada data</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div className="bg-white border-t border-[#E6D5B8] py-1 px-3 flex justify-between items-center text-[9px] mt-auto">
                    <span className="font-bold text-[#A39171]">TOTAL PACKAGING</span>
                    <span className="font-bold text-[#302621]">{formatCurrency(pkgTotal)}</span>
                  </div>
                </div>
                
                {/* Bahan Cair */}
                <div className="flex flex-col h-full">
                  <div className="bg-white py-1 px-3 text-[9px] font-bold text-[#302621] border-b border-[#E6D5B8] uppercase text-center">BAHAN CAIR</div>
                  <table className="w-full text-[9px]">
                    <thead className="bg-[#F9F5EC] text-[#7A695B] border-b border-[#E6D5B8]">
                      <tr>
                        <th className="py-1 px-2 text-left font-bold uppercase">No</th>
                        <th className="py-1 px-2 text-left font-bold uppercase">Item</th>
                        <th className="py-1 px-2 text-center font-bold uppercase">QTY</th>
                        <th className="py-1 px-2 text-right font-bold uppercase">Total Biaya (Rp)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brand.bahanCair.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 px-2 text-slate-400">{i+1}</td>
                          <td className="py-1 px-2 text-[#302621] break-words">{p.name}</td>
                          <td className="py-1 px-2 text-slate-500 text-center">{p.qty ? p.qty.toLocaleString('id-ID') : '-'}</td>
                          <td className="py-1 px-2 text-right text-[#302621]">{p.cost > 0 ? formatCurrency(p.cost).replace('Rp', '').trim() : '-'}</td>
                        </tr>
                      ))}
                      {brand.bahanCair.length === 0 && (
                        <tr><td colSpan={4} className="py-2 text-center text-slate-400 italic">Tidak ada data</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div className="bg-white border-t border-[#E6D5B8] py-1 px-3 flex justify-between items-center text-[9px] mt-auto">
                    <span className="font-bold text-[#A39171]">TOTAL BAHAN CAIR</span>
                    <span className="font-bold text-[#302621]">{formatCurrency(bcTotal)}</span>
                  </div>
                </div>
                
                {/* Item Tambahan */}
                <div className="flex flex-col h-full">
                  <div className="bg-white py-1 px-3 text-[9px] font-bold text-[#302621] border-b border-[#E6D5B8] uppercase text-center">ITEM TAMBAHAN</div>
                  <table className="w-full text-[9px]">
                    <thead className="bg-[#F9F5EC] text-[#7A695B] border-b border-[#E6D5B8]">
                      <tr>
                        <th className="py-1 px-2 text-left font-bold uppercase">No</th>
                        <th className="py-1 px-2 text-left font-bold uppercase">Item</th>
                        <th className="py-1 px-2 text-center font-bold uppercase">QTY</th>
                        <th className="py-1 px-2 text-right font-bold uppercase">Total Biaya (Rp)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brand.itemTambahan.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 px-2 text-slate-400">{i+1}</td>
                          <td className="py-1 px-2 text-[#302621] break-words">{p.name}</td>
                          <td className="py-1 px-2 text-slate-500 text-center">{p.qty ? p.qty.toLocaleString('id-ID') : '-'}</td>
                          <td className="py-1 px-2 text-right text-[#302621]">{p.cost > 0 ? formatCurrency(p.cost).replace('Rp', '').trim() : '-'}</td>
                        </tr>
                      ))}
                      {brand.itemTambahan.length === 0 && (
                        <tr><td colSpan={4} className="py-2 text-center text-slate-400 italic">-</td></tr>
                      )}
                    </tbody>
                  </table>
                  <div className="bg-white border-t border-[#E6D5B8] py-1 px-3 flex justify-between items-center text-[9px] mt-auto">
                    <span className="font-bold text-[#A39171]">TOTAL ITEM TAMBAHAN</span>
                    <span className="font-bold text-[#302621]">{itTotal > 0 ? formatCurrency(itTotal) : '-'}</span>
                  </div>
                </div>
              </div>
              
              {/* Brand Footer */}
              <div className="bg-[#F9F5EC] border-t border-[#E6D5B8] px-4 py-3 flex items-center justify-between text-xs">
                <span className="font-bold text-[#302621] uppercase">TOTAL KESELURUHAN BIAYA {(brand.name || '').toUpperCase()}</span>
                <div className="flex items-center gap-12">
                  <strong className="text-sm text-[#302621]">{formatCurrency(brand.totalBiaya)}</strong>
                  <span className="text-[#A39171] font-bold uppercase">HPP / pcs</span>
                  <strong className="text-sm text-[#302621]">{formatCurrency(hpp)}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* GRAND TOTAL */}
      <div className="mt-6 border border-[#E6D5B8] rounded-xl py-4 px-6 flex items-center justify-between bg-white shadow-sm page-break-inside-avoid">
        <div className="flex flex-col flex-1 items-start text-left pl-4">
          <span className="text-[10px] font-bold text-[#302621] uppercase mb-0.5">Total Produksi Seluruh Brand</span>
          <span className="text-lg font-black text-[#302621]">{totalAllProduksi.toLocaleString('id-ID')} pcs</span>
        </div>
        <div className="flex flex-col flex-1 items-center text-center border-l border-r border-[#E6D5B8]">
          <span className="text-[10px] font-bold text-[#302621] uppercase mb-0.5">Total Biaya Produksi Seluruh Brand</span>
          <span className="text-lg font-black text-[#302621]">{formatCurrency(totalBiayaProduksi)}</span>
        </div>
        <div className="flex flex-col flex-1 items-end text-right pr-4">
          <span className="text-[10px] font-bold text-[#302621] uppercase mb-0.5">Rata-rata HPP / pcs Seluruh Brand</span>
          <span className="text-lg font-black text-[#302621]">{formatCurrency(averageHPP)}</span>
        </div>
      </div>

      {/* FOOTER */}
      <div className="mt-8 flex items-start justify-between text-[10px]">
        <div className="w-1/2">
          <p className="font-bold text-[#302621] mb-1">Catatan:</p>
          <ol className="list-decimal pl-4 text-slate-500 space-y-1">
            <li>Semua nilai dalam laporan ini adalah estimasi berdasarkan data pada periode yang dipilih.</li>
            <li>HPP (Harga Pokok Produksi) dihitung dari Total Biaya Produksi dibagi Total Produksi.</li>
          </ol>
        </div>
        <div className="w-1/3 flex justify-between items-end border-l border-slate-200 pl-8">
          <div>
            <p className="text-slate-500 mb-6">Dicetak oleh,</p>
            <p className="font-bold text-[#302621]">Fadli</p>
            <p className="text-slate-400">Developer Web</p>
          </div>
          <div className="text-right">
            <p className="text-slate-400 flex items-center justify-end gap-2"><CalendarDays className="w-3.5 h-3.5 text-slate-400" /> Tanggal Cetak</p>
            <p className="font-bold text-[#302621] mt-1">{printDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
});

SimulasiProduksiReport.displayName = 'SimulasiProduksiReport';
